/**
 * @ngdoc service
 * @name runnerPublicFormApiService
 * @module flowingly.runner.services
 *
 * @description A helper API service for public form
 *
 * ## Notes
 *
 * ###API
 * * getFormById - get the form data by form guid
 * * submitForm - submit the form and then will complete the first step under the hood
 *
 */

import angular from 'angular';
import { SharedAngular } from '@Client/@types/sharedAngular';
import IApiPublicFormModel from '@Shared.Angular/@types/core/contracts/queryModel/api/apiPublicFormModel';
import { IApiExternalFormModel } from '@Shared.Angular/@types/core/contracts/queryModel/api/apiExternalFormModel';
import { Guid } from '@Shared.Angular/@types/guid';

interface ISubmitPublicFormRequest {
  publicFormId: string;
  cardData: string;
  stepId: Guid;
  subject: string;
  isTestFlow?: boolean;
}

interface ICompleteExternalFormRequest {
  cardData: string;
}

interface ISaveExternalFormRequest {
  formData: string;
}

export class RunnerPublicFormApiService {
  constructor(
    private $http: angular.IHttpService,
    private APP_CONFIG: SharedAngular.APP_CONFIG
  ) {}

  getFormById(flowModelId: string, inIframe: boolean, noSpinner: boolean) {
    return this.$http
      .get<IApiPublicFormModel>(
        `${this.APP_CONFIG.apiBaseUrl}form/${flowModelId}/${inIframe}`,
        {
          noSpinner: noSpinner || true
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  getExternalFormByStepId(stepId: Guid, noSpinner: boolean) {
    return this.$http
      .get<IApiExternalFormModel>(
        `${this.APP_CONFIG.apiBaseUrl}form/steps/${stepId}`,
        {
          noSpinner: noSpinner || true
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  submitForm(
    flowModelId: string,
    formData: ISubmitPublicFormRequest,
    noSpinner: boolean,
    isTestFlow = false
  ) {
    return this.$http
      .post<boolean | Guid>(
        `${this.APP_CONFIG.apiBaseUrl}form/${flowModelId}${isTestFlow ? '/test' : ''}`,
        formData,
        { noSpinner: noSpinner || false }
      )
      .then((response) => response.data);
  }

  submitExternalForm(
    stepId: Guid,
    formData: ICompleteExternalFormRequest,
    noSpinner: boolean
  ) {
    return this.$http.post<void>(
      `${this.APP_CONFIG.apiBaseUrl}form/steps/${stepId}`,
      formData,
      { noSpinner: noSpinner || false }
    );
  }

  saveForm(stepId: string, formData: unknown) {
    const request: ISaveExternalFormRequest = {
      formData: JSON.stringify(formData)
    };
    return this.$http.put<void>(
      `${this.APP_CONFIG.apiBaseUrl}form/steps/${stepId}`,
      request
    );
  }

  uploadAnonymousFile(
    formData: FormData,
    flowModelId: string,
    stepId: string,
    fieldKey: string
  ) {
    return this.$http.post<string>(
      `${this.APP_CONFIG.apiBaseUrl}form/${flowModelId}/step/${stepId}/field/${fieldKey}/file`,
      formData,
      {
        transformRequest: angular.identity,
        timeout: 90000,
        headers: { 'Content-Type': undefined }
      }
    );
  }

  deleteAnonymousFile(fileId: string, stepId: string) {
    return this.$http.delete<void>(
      `${this.APP_CONFIG.apiBaseUrl}form/step/${stepId}/file/${fileId}`
    );
  }
}

angular
  .module('flowingly.runner.services')
  .factory('runnerPublicFormApiService', [
    '$http',
    'APP_CONFIG',
    ($http: angular.IHttpService, APP_CONFIG: SharedAngular.APP_CONFIG) =>
      new RunnerPublicFormApiService($http, APP_CONFIG)
  ]);

export type RunnerPublicFormApiServiceType = InstanceType<
  typeof RunnerPublicFormApiService
>;
